import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { IInputTcDetails } from "../../../../tc-models/tc-req-dto/input-tc-product.req_dto";
import { Option } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useFormik } from "formik";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { Box, Typography } from "@mui/material";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import _ from "lodash";

interface CertifiedRawMaterialData {
  inputTcCertifiedRawMaterialCode: string;
  outputTcCertifiedRawMaterialCode: string;
  inputTcCertifiedRawMaterialWeightUsed: number | null;
  inputTcCertifiedRawMaterialCodeDescription?: string;
  outputTcCertifiedRawMaterialCodeDescription?: string;
}

interface InputCertifiedRawMaterialDetailsProps {
  certifiedRawMaterialData: CertifiedRawMaterialData[];
  setCertifiedRawMaterialData: React.Dispatch<React.SetStateAction<CertifiedRawMaterialData[]>>;
  inputTcDetails: IInputTcDetails;
  isTC?: boolean | null;
}

const InputCertifiedRawMaterialDetails: React.FC<InputCertifiedRawMaterialDetailsProps> = ({
  certifiedRawMaterialData,
  setCertifiedRawMaterialData,
  inputTcDetails,
  isTC,
}) => {
  const { TE_ID } = useParams<string>();
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [selectedProductRawMaterialCodes, setSelectedProductRawMaterialCodes] = useState<string[]>([]);
  const [selectedAssociatedProductNo, setSelectedAssociatedProductNo] = useState<string>(
    inputTcDetails?.tcAssociatedProductNo || ""
  );
  const { executeGet } = useApi();
  const [rawMaterialCodes, setRawMaterialCodes] = useState<any[]>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [data, setData] = useState<any>(certifiedRawMaterialData ?? []);
  const [tableData, setTableData] = useState<any>(certifiedRawMaterialData ?? []);
  const { isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const { notify } = useNotification();
  const [isInputTcNoNA, setIsInputTcNoNA] = useState(
    inputTcDetails?.inputTcNo === "N/A"
  );

  const fetchProductDetails = async () => {
    // Don't call TC-related APIs if ATC is selected
    if (isTC === false) {
      // Set empty product details for ATC flow
      setProductDetails([]);
      return;
    }
    
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST,
        { tcId: TE_ID }
      );

      // Save the full response data for later use
      const fullResponseData = response?.data?.response || [];
      setProductDetails(fullResponseData);
    } catch (err) {
      console.error("Failed to fetch Product Details:", err);
    }
  };

  const handleAssociatedProductNoChange = (selectedValue: string) => {
    // Save the selected associated product number in state
    setSelectedAssociatedProductNo(selectedValue);
    
    // Find the selected product details
    const selectedProduct = productDetails.find(
      (product: any) => product.tcProductNo === selectedValue
    );
    
    // Extract and save the tcProductRawMaterialCode for the selected product
    if (selectedProduct && selectedProduct.tcProductRawMaterialCode) {
      setSelectedProductRawMaterialCodes(selectedProduct.tcProductRawMaterialCode);
    } else {
      setSelectedProductRawMaterialCodes([]);
    }

    // Reset output fields when product changes
    setData((prevData: any[]) => {
      return prevData.map((row: any) => {
        const updatedRow = { ...row };
        
        // If the current input code is not in the new selected codes, reset the output
        if (row.inputTcCertifiedRawMaterialCode && 
            !selectedProduct?.tcProductRawMaterialCode?.includes(row.inputTcCertifiedRawMaterialCode)) {
          updatedRow.outputTcCertifiedRawMaterialCode = "";
        }
        
        return updatedRow;
      });
    });
  };

  useEffect(() => {
    if (TE_ID) {
      fetchProductDetails();
    }
    
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();

        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          description: t.description_1,
        }));
        setRawMaterialCodes(rawMaterials);
      } catch (err) {
        console.error(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    getRawMaterialCodes();
  }, []);

  // Handle initialization of selected product raw material codes when component loads with existing data
  useEffect(() => {
    if (selectedAssociatedProductNo && productDetails.length > 0) {
      const selectedProduct = productDetails.find(
        (product: any) => product.tcProductNo === selectedAssociatedProductNo
      );
      
      if (selectedProduct && selectedProduct.tcProductRawMaterialCode) {
        setSelectedProductRawMaterialCodes(selectedProduct.tcProductRawMaterialCode);
      }
    }
  }, [selectedAssociatedProductNo, productDetails]);

  // Update table data when certifiedRawMaterialData changes
  useEffect(() => {
    if (certifiedRawMaterialData) {
      // Auto-populate outputTcCertifiedRawMaterialCode if it's empty but inputTcCertifiedRawMaterialCode exists
      const processedData = certifiedRawMaterialData.map((item: any) => {
        if (item.inputTcCertifiedRawMaterialCode && !item.outputTcCertifiedRawMaterialCode) {
          // For ATC flow, always auto-populate; for TC flow, check if code is in selected product codes
          if (isTC === false || selectedProductRawMaterialCodes.includes(item.inputTcCertifiedRawMaterialCode)) {
            return {
              ...item,
              outputTcCertifiedRawMaterialCode: item.inputTcCertifiedRawMaterialCode
            };
          }
        }
        return item;
      });
      
      setData(processedData);
      setTableData(processedData);
    }
  }, [certifiedRawMaterialData, selectedProductRawMaterialCodes]);

  // Update selected associated product number when inputTcDetails changes
  useEffect(() => {
    if (inputTcDetails?.tcAssociatedProductNo) {
      setSelectedAssociatedProductNo(inputTcDetails.tcAssociatedProductNo);
      handleAssociatedProductNoChange(inputTcDetails.tcAssociatedProductNo);
    }
  }, [inputTcDetails?.tcAssociatedProductNo]);

  // Update isInputTcNoNA when inputTcDetails changes
  useEffect(() => {
    if (inputTcDetails?.inputTcNo) {
      setIsInputTcNoNA(inputTcDetails.inputTcNo === "N/A");
    }
  }, [inputTcDetails?.inputTcNo]);

  const columns: EditableColumnDef<any>[] = [
    {
      accessorKey: "inputTcCertifiedRawMaterialCode",
      header: "Input Certified Raw Material Code",
      Header: (
        <span>
          {!isInputTcNoNA && inputTcDetails.inputTCFlag === "Y" && <i style={{ color: "red" }}>* </i>}{" "}
          Input Certified Raw Material Code
        </span>
      ),
      enableHiding: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="inputTcCertifiedRawMaterialCode"
          enableSearch={true}
          value={value}
          options={rawMaterialCodes}
          required={!isInputTcNoNA}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "outputTcCertifiedRawMaterialCode",
      header: "Output Certified Raw Material Code",
      Header: (
        <span>
          {" "}
          {inputTcDetails.inputTCFlag === "Y" && <i style={{ color: "red" }}>* </i>} Output Certified Raw Material Code
        </span>
      ),
      enableHiding: false,
      renderEditCell: ({ value, onChange }) => {
        // For ATC flow, show all raw material codes; for TC flow, filter by selected product codes
        const filteredOptions = isTC === false 
          ? rawMaterialCodes  // Show all options for ATC
          : rawMaterialCodes.filter((option: any) => 
              selectedProductRawMaterialCodes.includes(option.value)
            );
        
        return (
          <CustomSelect
            label=""
            name="outputTcCertifiedRawMaterialCode"
            enableSearch={true}
            value={value}
            options={filteredOptions}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        );
      },
      minSize: 60,
      maxSize: 150,
    },
    {
      accessorKey: "inputTcCertifiedRawMaterialWeightUsed",
      header: "Input Certified Raw Material Weight Used (kg)",
      Header: (
        <span>
          {!isInputTcNoNA && inputTcDetails.inputTCFlag === "Y" && <i style={{ color: "red" }}>* </i>}{" "}
          Input Certified Raw Material Weight Used (kg)
        </span>
      ),
      enableHiding: false,
      minSize: 60,
      maxSize: 150,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcCertifiedRawMaterialWeightUsed"
          value={value}
          required={!isInputTcNoNA}
          inputProps={{
            max: 100,
            step: "0.01",
          }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === "") {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const formik = useFormik<any>({
    initialValues: {
      inputTcCertifiedRawMaterialCode: "",
      outputTcCertifiedRawMaterialCode: "",
      inputTcCertifiedRawMaterialWeightUsed: null,
    },
    validationSchema: Yup.object().shape({
      inputTcCertifiedRawMaterialCode: inputTcDetails.inputTCFlag === "N" 
        ? Yup.string() 
        : Yup.string().required(),
      outputTcCertifiedRawMaterialCode: inputTcDetails.inputTCFlag === "N" 
        ? Yup.string() 
        : Yup.string().required(),
      inputTcCertifiedRawMaterialWeightUsed: inputTcDetails.inputTCFlag === "N" 
        ? Yup.number() 
        : Yup.number().required(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);

    setData((prevData: any[]) => {
      const updatedData = prevData.map((row, index) => {
        if (index === rowIndex) {
          const updatedRow = { ...row, [columnId]: value };
          
          // If inputTcCertifiedRawMaterialCode is being changed
          if (columnId === "inputTcCertifiedRawMaterialCode") {
            // For ATC flow, always auto-populate; for TC flow, check if code is in selected product codes
            if (isTC === false || selectedProductRawMaterialCodes.includes(value)) {
              // Auto-populate outputTcCertifiedRawMaterialCode with the same value
              updatedRow.outputTcCertifiedRawMaterialCode = value;
              formik.setFieldValue("outputTcCertifiedRawMaterialCode", value);
            } else {
              // Clear output if the input code is not in the selected codes (TC flow only)
              updatedRow.outputTcCertifiedRawMaterialCode = "";
              formik.setFieldValue("outputTcCertifiedRawMaterialCode", "");
            }
          }
          
          return updatedRow;
        }
        return row;
      });
      
      return updatedData;
    });
  };

  const handleAddNewRow = () => {
    const newRow: any = {
      inputTcCertifiedRawMaterialCode: "",
      outputTcCertifiedRawMaterialCode: "",
      inputTcCertifiedRawMaterialWeightUsed: null,
      inputTcCertifiedRawMaterialCodeDescription: "",
      outputTcCertifiedRawMaterialCodeDescription: "",
    };
    setData((prevData: any) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    if (editingRowIndex !== null) {
      setData(tableData);
    }
    setEditingRowIndex(null);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    const currentRowValues = formik.values;
    
    let isValid = true;
    let errors: any = {};

    // Only validate these fields if inputTcNo is not "N/A"
    if (!isInputTcNoNA) {
      if (!currentRowValues.inputTcCertifiedRawMaterialCode) {
        errors.inputTcCertifiedRawMaterialCode = "Input Certified Raw Material Code is required";
        isValid = false;
      }
      if (!currentRowValues.inputTcCertifiedRawMaterialWeightUsed) {
        errors.inputTcCertifiedRawMaterialWeightUsed = "Input Certified Raw Material Weight Used is required";
        isValid = false;
      }
      if (!currentRowValues.outputTcCertifiedRawMaterialCode) {
        errors.outputTcCertifiedRawMaterialCode = "Output Certified Raw Material Code is required";
        isValid = false;
      }
    }

    if (!isValid) {
      formik.setErrors(errors);
      return;
    }

    const updatedRow = {
      ...currentRowValues,
      inputTcCertifiedRawMaterialCodeDescription: rawMaterialCodes?.find(
        (codes: any) =>
          codes?.value === currentRowValues?.inputTcCertifiedRawMaterialCode
      )?.description,
      outputTcCertifiedRawMaterialCodeDescription: rawMaterialCodes?.find(
        (codes: any) =>
          codes?.value === currentRowValues?.outputTcCertifiedRawMaterialCode
      )?.description,
    };
    const newData = data.map((row: any, index: number) =>
      index === editingRowIndex ? updatedRow : row
    );
    setData(newData);
    setTableData(newData);
    setCertifiedRawMaterialData(newData);
    setEditingRowIndex(null);
    setRestrictPopUpSave(false);

    formik.resetForm();
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter(
      (_: any, index: number) => index !== rowIndex
    );
    setData(updatedData);
    setTableData(updatedData);
    setCertifiedRawMaterialData(updatedData);
  };

  return (
    <>
      <Box display="flex" mb={3} justifyContent="space-between" width="100%">
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
          Input Certified Raw Material Used Details
        </Typography>
        <StyledButton
          disabled={isRestrictPopUpSave || !selectedAssociatedProductNo}
          onClick={handleAddNewRow}
          sx={{ mb: 2 }}
        >
          Add Raw Material
        </StyledButton>
      </Box>
      <EditableTable
        data={data}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
        enableEditing={!!selectedAssociatedProductNo}
      />
    </>
  );
};

export default InputCertifiedRawMaterialDetails;
